import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Sprint 6.9.9 — Trésorerie prévisionnelle agrégée pour le Tableau de bord.
// Calcule les 3 montants clés + le solde net :
//   - À recevoir (recouvrement entreprises B2B non soldé)
//   - Dette formateurs (paiements_formateurs statut='a_payer')
//   - Dette fournisseurs (dettes_fournisseurs non soldés)
//   - Net = à recevoir − (dette formateurs + dette fournisseurs)

export interface TresoreriePrevisionnelle {
  /** Reste à encaisser sur factures B2B = SUM(montant_ttc - montant_encaisse) WHERE statut != 'regle' */
  aRecevoir: number;
  /** Dette envers les formateurs = SUM(montant) WHERE statut = 'a_payer' */
  detteFormateurs: number;
  /** Dette envers les fournisseurs = SUM(montant_dette - montant_regle) WHERE statut != 'paye' */
  detteFournisseurs: number;
  /** Solde net prévisionnel = aRecevoir − detteFormateurs − detteFournisseurs */
  soldeNet: number;
  /** Compteurs pour affichage UX */
  nbFacturesEnCours: number;
  nbPaiementsFormateurs: number;
  nbDettesFournisseurs: number;
}

interface RecouvrementRow {
  montant_ttc: number;
  montant_encaisse: number;
  statut: string;
}
interface PaiementFormateurRow {
  montant: number;
  statut: string;
}
interface DetteFournisseurRow {
  montant_dette: number;
  montant_regle: number;
  statut: string;
}

export function useTresoreriePrevisionnelle(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['tresorerie_previsionnelle', entrepriseId ?? '__all__'],
    queryFn: async (): Promise<TresoreriePrevisionnelle> => {
      // 3 queries parallélisées
      let recQ = supabase
        .from('recouvrement_entreprises')
        .select('montant_ttc, montant_encaisse, statut')
        .neq('statut', 'regle');
      let formQ = supabase
        .from('paiements_formateurs')
        .select('montant, statut')
        .eq('statut', 'a_payer');
      let fournQ = supabase
        .from('dettes_fournisseurs')
        .select('montant_dette, montant_regle, statut')
        .neq('statut', 'paye');

      if (entrepriseId) {
        recQ = recQ.eq('entreprise_id', entrepriseId);
        formQ = formQ.eq('entreprise_id', entrepriseId);
        fournQ = fournQ.eq('entreprise_id', entrepriseId);
      }

      const [recRes, formRes, fournRes] = await Promise.all([recQ, formQ, fournQ]);
      if (recRes.error) throw recRes.error;
      if (formRes.error) throw formRes.error;
      if (fournRes.error) throw fournRes.error;

      const recRows = (recRes.data ?? []) as RecouvrementRow[];
      const formRows = (formRes.data ?? []) as PaiementFormateurRow[];
      const fournRows = (fournRes.data ?? []) as DetteFournisseurRow[];

      const aRecevoir = recRows.reduce(
        (acc, r) => acc + (Number(r.montant_ttc) - Number(r.montant_encaisse)),
        0,
      );
      const detteFormateurs = formRows.reduce((acc, r) => acc + Number(r.montant), 0);
      const detteFournisseurs = fournRows.reduce(
        (acc, r) => acc + (Number(r.montant_dette) - Number(r.montant_regle)),
        0,
      );

      return {
        aRecevoir,
        detteFormateurs,
        detteFournisseurs,
        soldeNet: aRecevoir - detteFormateurs - detteFournisseurs,
        nbFacturesEnCours: recRows.length,
        nbPaiementsFormateurs: formRows.length,
        nbDettesFournisseurs: fournRows.length,
      };
    },
  });
}
